import { REPEAT_END_TYPE_OPTIONS } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/session-repeat-rule-editor/session-repeat-rule-editor-constants";
import { DAYS_OF_WEEK_LOWERCASE_LENGTH_3 } from "constants/daysOfWeek";
import moment from "moment-timezone";
import getNextEventOnNthDayOfWeekInMonthMoment from "./getNthDayOfWeekInMonth";

// old template used in deprecated single appointment invite API
type Template = {
  /** Timeslot Length Minute */
  timeslot_length_minute: number;
  /** Timeslot Datetime Start */
  timeslot_datetime_start: string;
  /** Appointment Held Place */
  place_session_held_outside_of_orchid?: string;
  /** External Meeting Link */
  external_meeting_link?: string;
};

const getAppointmentsRepeatedOnNthDayOfWeekInMonth = ({
  repeatRule,
  template,
  timezone,
}: {
  repeatRule: RepeatRule;
  template: Template;
  timezone: string;
}) => {
  if (
    repeatRule.repeat_interval !== "month" ||
    !repeatRule.repeat_on_nth_day_of_week_in_month
  ) {
    throw new Error(
      "getAppointmentsRepeatedOnNthDayOfWeekInMonth can only be called for events repeating monthly on nth day of week in the month",
    );
  }

  const appointments: InviteSeriesAppointment[] = [];

  const {
    timeslot_datetime_start: templateStart,
    timeslot_length_minute: templateLength,
  } = template;

  let dateCursor = moment.tz(templateStart, timezone);
  const {
    repeat_every,
    repeat_on_nth_day_of_week_in_month: { nth, day_of_week },
  } = repeatRule;

  if (repeatRule.repeat_end.type === REPEAT_END_TYPE_OPTIONS.end_date.id) {
    const endDate = moment
      .tz(repeatRule.repeat_end.end_date, timezone)
      .endOf("day");

    do {
      const end = dateCursor.clone().add(templateLength, "minute");
      appointments.push({
        ...template,
        appointment_start: dateCursor.toISOString(),
        appointment_end: end.toISOString(),
      });

      dateCursor = getNextEventOnNthDayOfWeekInMonthMoment({
        currentEventStart: dateCursor,
        nth: nth,
        dayOfWeek: day_of_week,
        skipMonths: repeat_every,
        timezone: timezone,
      });
    } while (dateCursor.isBefore(endDate));

    return appointments;
  }

  for (let i = 0; i < repeatRule.repeat_end.end_after; i++) {
    const end = dateCursor.clone().add(templateLength, "minute");
    appointments.push({
      ...template,
      appointment_start: dateCursor.toISOString(),
      appointment_end: end.toISOString(),
    });

    dateCursor = getNextEventOnNthDayOfWeekInMonthMoment({
      currentEventStart: dateCursor,
      nth: nth,
      dayOfWeek: day_of_week,
      skipMonths: repeat_every,
      timezone: timezone,
    });
  }
  return appointments;
};

/**
 * get appointments by repeat rule for creating appointment series
 */
export function getAppointmentsByRepeatRule({
  repeatRule,
  timezone,
  template,
}: {
  repeatRule: RepeatRule;
  timezone: string;
  template: Template;
}) {
  const appointments: InviteSeriesAppointment[] = [];

  const {
    timeslot_datetime_start: templateStart,
    timeslot_length_minute: templateLength,
    ...restTemplate
  } = template;
  // repeat by year, month
  if (
    repeatRule.repeat_interval === "year" ||
    repeatRule.repeat_interval === "month"
  ) {
    if (
      repeatRule.repeat_interval === "month" &&
      repeatRule.repeat_on_nth_day_of_week_in_month
    ) {
      return getAppointmentsRepeatedOnNthDayOfWeekInMonth({
        repeatRule,
        template,
        timezone,
      });
    }

    let count = 0;

    // get the count by teh repeat type
    if (repeatRule.repeat_end.type === REPEAT_END_TYPE_OPTIONS.end_date.id) {
      const a = moment.tz(template.timeslot_datetime_start, timezone);
      const b = moment.tz(repeatRule.repeat_end.end_date, timezone);
      count = b.diff(a, repeatRule.repeat_interval);
    } else if (
      repeatRule.repeat_end.type === REPEAT_END_TYPE_OPTIONS.end_after.id
    ) {
      count = repeatRule.repeat_end.end_after;
    }

    for (let i = 0; i < count; i++) {
      const start = moment
        .tz(templateStart, timezone)
        .add(i * repeatRule.repeat_every, repeatRule.repeat_interval);
      const end = start.clone().add(templateLength, "minute");
      appointments.push({
        ...restTemplate,
        appointment_start: start.toISOString(),
        appointment_end: end.toISOString(),
      });
    }

    return appointments;
  }

  // repeat weekly
  return getAppointmentsRepeatByWeekByRepeatRule({
    timezone,
    templateForWeek: getWeeklyAppointmentsTemplate({
      template,
      timezone,
      repeatOnDays: repeatRule.repeat_on_days,
    }),
    startValue: +new Date(template.timeslot_datetime_start),
    repeatEvery: repeatRule.repeat_every,
    repeatRule,
  });
}

/**
 * get a week's appointment by repeat on days settings
 */
function getWeeklyAppointmentsTemplate({
  template: {
    timeslot_datetime_start: templateStart,
    timeslot_length_minute: templateLength,
    ...restTemplate
  },
  repeatOnDays,
  timezone,
}: {
  template: Template;
  repeatOnDays: string[];
  timezone: TimeZone;
}) {
  return DAYS_OF_WEEK_LOWERCASE_LENGTH_3.reduce((prev, curr, index) => {
    const weekday = moment.tz(templateStart, timezone).days();
    const weekStartWithTime = moment
      .tz(templateStart, timezone)
      .add(-weekday, "days")
      .toISOString();
    const includesDay = repeatOnDays.includes(curr);
    if (includesDay) {
      const appointment_start = moment
        .tz(weekStartWithTime, timezone)
        .add(index, "day")
        .toISOString();
      const appointment_end = moment
        .tz(weekStartWithTime, timezone)
        .add(index, "day")
        .add(templateLength, "minute")
        .toISOString();

      prev.push({
        ...restTemplate,
        appointment_start,
        appointment_end,
      });
    }
    return prev;
  }, [] as InviteSeriesAppointment[]);
}

/**
 * get appointments repeat by weeks by repeat rule
 */
function getAppointmentsRepeatByWeekByRepeatRule({
  startValue,
  templateForWeek,
  repeatEvery,
  timezone,
  repeatRule,
}: {
  startValue: number;
  templateForWeek: InviteSeriesAppointment[];
  repeatEvery: number;
  timezone: TimeZone;
  repeatRule: RepeatRule;
}): InviteSeriesAppointment[] {
  const res: InviteSeriesAppointment[] = [];

  // index of the nth recurring week
  let i = 0;
  if (repeatRule.repeat_end.type === REPEAT_END_TYPE_OPTIONS.end_after.id) {
    while (res.length < repeatRule.repeat_end.end_after) {
      for (const interval of templateForWeek) {
        const nextInterval = getNthWeekAppointment({
          appointment: interval,
          timezone,
          weekCount: i * repeatEvery,
        });
        if (
          +new Date(nextInterval.appointment_start) >= startValue &&
          res.length < repeatRule.repeat_end.end_after
        ) {
          res.push(nextInterval);
        }
      }
      i++;
    }
  } else if (
    repeatRule.repeat_end.type === REPEAT_END_TYPE_OPTIONS.end_date.id
  ) {
    const endDateValue = +new Date(repeatRule.repeat_end.end_date);
    let stop = false;
    while (!stop) {
      for (const interval of templateForWeek) {
        const nextInterval = getNthWeekAppointment({
          appointment: interval,
          timezone,
          weekCount: i * repeatEvery,
        });

        if (+new Date(nextInterval.appointment_start) >= endDateValue) {
          stop = true;
        }

        if (
          +new Date(nextInterval.appointment_start) >= startValue &&
          +new Date(nextInterval.appointment_end) <= endDateValue
        ) {
          res.push(nextInterval);
        }
      }
      i++;
    }
  }

  return res;
}

/**
 * get nth week appointment
 * @example
 * getNthWeekAppointment({
 *   appointment: {
 *     appointment_start: "2023-02-12T05:00:00.000Z",
 *     appointment_end: "2023-02-12T65:00:00.000Z",
 *   },
 *   timezone: "UTC",
 *   weekCount: 2
 * })
 * -> {
 *      appointment_start: "2023-02-26T05:00:00.000Z",
 *      appointment_end: "2023-02-26T06:00:00.000Z",
 *    }
 */
function getNthWeekAppointment({
  appointment,
  timezone,
  weekCount,
}: {
  appointment: InviteSeriesAppointment;
  timezone: TimeZone;
  weekCount: number;
}): InviteSeriesAppointment & { appointment_end: string } {
  const appointment_start = moment(appointment.appointment_start)
    .tz(timezone)
    .add(weekCount, "week")
    .toISOString();
  const appointment_end = moment(appointment.appointment_end)
    .tz(timezone)
    .add(weekCount, "week")
    .toISOString();

  return {
    ...appointment,
    appointment_start,
    appointment_end,
  };
}
